import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Spinner } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';

import BankIcon from './BankIcon';

const PlaidConnectionList = ({ projectId, isEditor, onUpdate, onUpdateAccounts, refreshTrigger, onDiscoverBank }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/projects/${projectId}/plaid/connections`);
      setConnections(res.data);
    } catch (err) {
      console.error('Error fetching connections:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections, refreshTrigger]);

  const handleDisconnect = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this bank? This will stop transaction syncing and unlink all associated accounts in this app. This action also removes the connection from Plaid to stop any further billing.')) {
      return;
    }

    setActionLoading(connectionId);
    try {
      await axios.delete(`/api/projects/${projectId}/plaid/connections/${connectionId}`);
      fetchConnections();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error disconnecting:', err);
      alert('Failed to disconnect bank.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="card-title font-weight-bold text-primary mb-0">
            <i className="fas fa-plug mr-2"></i> Active Bank Connections
          </h5>
          {isEditor && connections.length > 0 && (
            <Button 
              variant="primary" 
              size="sm" 
              className="px-3 py-2 font-weight-bold"
              style={{ borderRadius: '6px' }}
              onClick={onDiscoverBank}
            >
              <i className="fas fa-plus mr-1"></i> Connect a Bank
            </Button>
          )}
        </div>
        
        {connections.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-university fa-3x mb-3 opacity-20"></i>
            <p>No active bank connections found for this project.</p>
            {isEditor && (
              <Button 
                variant="primary" 
                className="mt-2 px-4 py-2 font-weight-bold"
                style={{ borderRadius: '8px' }}
                onClick={onDiscoverBank}
              >
                <i className="fas fa-plug mr-2"></i> Connect a Bank
              </Button>
            )}
          </div>
        ) : (
          <div className="row">
            {connections.map(conn => {
              return (
                <div key={conn._id} className="col-12 mb-3">
                  <div className="card shadow-sm border" style={{ borderRadius: '12px', borderColor: '#e9ecef', backgroundColor: '#fff' }}>
                    <div className="card-body p-4">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-center mb-3">
                            <div className="mr-3">
                              <BankIcon institutionName={conn.institutionName} logo={conn.logo} size="48px" />
                            </div>
                            <div>
                              <h5 className="mb-0 font-weight-bold text-dark">{conn.institutionName || 'Unknown Bank'}</h5>
                              <small className="text-muted">Linked via Plaid</small>
                            </div>
                            <div className="ml-3">
                              <span className={`badge badge-${conn.status === 'healthy' ? 'success' : 'danger'} px-3 py-2 text-uppercase`} style={{ borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600' }}>
                                <span className="mr-2 d-inline-block" style={{ width: '6px', height: '6px', backgroundColor: 'currentColor', borderRadius: '50%', verticalAlign: 'middle' }}></span>
                                {conn.status === 'healthy' ? 'Active' : conn.status}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="text-muted small mb-2 font-weight-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>Linked Accounts ({conn.accounts?.length || 0})</div>
                            <div className="d-flex flex-wrap">
                              {conn.accounts && conn.accounts.length > 0 ? (
                                conn.accounts.map(acc => (
                                  <span key={acc.id} className="badge badge-info mr-2 mb-2 px-3 py-2 font-weight-normal" style={{ fontSize: '0.8rem', borderRadius: '20px', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
                                    {acc.name}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted small italic">No accounts linked</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4 text-md-right mt-3 mt-md-0 border-left pl-md-4" style={{ borderColor: '#e9ecef' }}>
                          <div className="small text-muted mb-3">
                            Connected on: {moment(conn.createdAt).format('MMM D, YYYY')}
                          </div>
                          {isEditor && (
                            <div className="d-flex justify-content-md-end align-items-center">
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="mr-2 px-3 py-2 font-weight-bold"
                                style={{ borderRadius: '6px' }}
                                onClick={() => onUpdateAccounts && onUpdateAccounts(conn._id)}
                              >
                                <i className="fas fa-edit mr-1"></i> Update
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                className="px-3 py-2 font-weight-bold"
                                style={{ borderRadius: '6px' }}
                                onClick={() => handleDisconnect(conn._id)}
                                disabled={actionLoading === conn._id}
                              >
                                {actionLoading === conn._id ? <Spinner size="sm" animation="border" /> : <><i className="fas fa-unlink mr-1"></i> Disconnect</>}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-light rounded border-left border-info" style={{ borderLeftWidth: '4px' }}>
          <p className="small mb-0 text-muted">
            <i className="fas fa-info-circle mr-2 text-info"></i>
            <strong>Cost Management Tip:</strong> Plaid charges per unique bank connection (Item). 
            If you see duplicate connections to the same institution, disconnect the unused ones to stay within your trial limits and minimize costs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlaidConnectionList;
